import stringHelper from './stringHelper';

/**
 * Builds the message shown after a bulk action on a list page.
 *
 * A bulk action posts the ids the user picked, but the backend re-checks every row and acts only
 * on the ones still in an eligible status. Reporting the returned count as a plain success hides
 * that: a selection that has gone stale ends up as "Zero assignment has been ..." inside a success
 * dialog. So the caller gets both the sentence and whether it is really a success, and maps that to
 * a success or a warning popup.
 *
 * `done` is the count the backend reports, `requested` the number of eligible ids the client
 * actually sent (unknown - null - when the whole search set was selected, in which case a partial
 * result cannot be detected). `actionDescription` is the past-tense phrase, e.g. "attached to a
 * vendor invoice".
 */
export function buildBulkActionResult({
  done,
  requested = null,
  allInSetSelected = false,
  actionDescription,
  itemTypeName = 'assignment',
  itemTypeNamePlural = null,
}: {
  done: number;
  requested?: number | null;
  allInSetSelected?: boolean;
  actionDescription: string;
  itemTypeName?: string;
  itemTypeNamePlural?: string | null;
}): { isSuccess: boolean; message: string } {
  const pluralName: string = itemTypeNamePlural ? itemTypeNamePlural : `${itemTypeName}s`;
  const staleHint = 'refresh the list to see the current status.';

  if (!done || done <= 0) {
    return {
      isSuccess: false,
      message: `No ${itemTypeName} has been ${actionDescription}. Their status may have changed in the meantime - ${staleHint}`,
    };
  }

  const fullSuccessMessage = `${stringHelper.plural(
    done,
    itemTypeName,
    pluralName,
    true,
    true
  )} been ${actionDescription}.`;

  if (allInSetSelected || requested === null || requested === undefined || done >= requested) {
    return { isSuccess: true, message: fullSuccessMessage };
  }

  const skipped: number = requested - done;
  const skippedStatus = skipped > 1 ? 'their status has' : 'its status has';
  return {
    isSuccess: false,
    message:
      `${stringHelper.numberWithLetter(done, true)} of ${stringHelper.numberWithLetter(requested)} ` +
      `${pluralName} have been ${actionDescription}. ${stringHelper.numberWithLetter(skipped, true)} could not be ` +
      `${actionDescription} because ${skippedStatus} changed in the meantime - ${staleHint}`,
  };
}
